<template>
  <div class="page">
    <van-nav-bar
      title="Mandate Detail"
      left-arrow
      @click-left="$router.back()"
    />
    <CommonLoading ref="commonLoader"></CommonLoading>

    <div v-if="detailLoading" style="text-align:center; padding: 60px 0; color:#969799;">
      Loading...
    </div>

    <div v-else-if="!detail" style="text-align:center; padding: 60px 0; color:#969799;">
      记录不存在
    </div>

    <div v-else class="content">

      <!-- Header summary -->
      <div class="headerCard">
        <div class="headerTop">
          <div class="symbol">{{ detail.symbol }}</div>
          <van-tag :type="statusTagType(detail.status)" size="medium">{{ detail.status }}</van-tag>
        </div>
        <div class="reference">{{ detail.reference }}</div>

        <div class="priceRow">
          <div class="priceBlock">
            <div class="priceLabel">Mark Price</div>
            <div class="priceValue">{{ detail.mark_price }}</div>
          </div>
          <div class="priceBlock">
            <div class="priceLabel">Baseline Price</div>
            <div class="priceValue">{{ detail.baseline_price }}</div>
          </div>
          <div class="priceBlock">
            <div class="priceLabel">Portfolio P/L</div>
            <div class="priceValue" :class="pnlClass(detail.pnl_summary.portfolio_pnl)">
              {{ formatPnl(detail.pnl_summary.portfolio_pnl) }}
            </div>
          </div>
        </div>

        <div class="datesRow">
          <span>{{ detail.start_date }} → {{ detail.end_date }}</span>
          <span>·</span>
          <span>{{ detail.duration_days }} days</span>
          <span>·</span>
          <van-tag plain :type="riskTagType(detail.risk_level)">{{ detail.risk_level }}</van-tag>
        </div>
      </div>

      <!-- Position stats -->
      <div class="sectionCard">
        <div class="sectionTitle">Position</div>
        <div class="statGrid">
          <div class="statItem">
            <div class="statLabel">Entrusted Shares</div>
            <div class="statValue">{{ detail.entrusted_shares }}</div>
          </div>
          <div class="statItem">
            <div class="statLabel">Current Shares</div>
            <div class="statValue">{{ detail.current_shares }}</div>
          </div>
          <div class="statItem">
            <div class="statLabel">Additional Capital</div>
            <div class="statValue">{{ detail.additional_capital }}</div>
          </div>
          <div class="statItem">
            <div class="statLabel">Cash Balance</div>
            <div class="statValue">{{ detail.cash_balance }}</div>
          </div>
          <div class="statItem">
            <div class="statLabel">Realized P/L</div>
            <div class="statValue" :class="pnlClass(detail.realized_pnl)">{{ formatPnl(detail.realized_pnl) }}</div>
          </div>
          <div class="statItem">
            <div class="statLabel">Awaiting Signals</div>
            <div class="statValue">{{ detail.awaiting_signals ? 'Yes' : 'No' }}</div>
          </div>
        </div>
      </div>

      <!-- Intraday chart -->
      <div class="sectionCard" v-if="chartPoints.length">
        <div class="sectionTitle">
          Intraday Chart
          <span class="chartInterval">{{ detail.recent_chart.interval }}</span>
        </div>

        <div class="chartPriceRow">
          <span class="chartLivePrice" :class="livePriceClass">{{ detail.recent_chart.live_price }}</span>
          <span class="chartLiveLabel">Live</span>
        </div>

        <div class="chartWrap" ref="chartWrap">
          <svg
            :viewBox="`0 0 ${chartWidth} ${chartHeight}`"
            class="chartSvg"
            preserveAspectRatio="none"
            @touchstart="onChartTouch"
            @touchmove="onChartTouch"
            @touchend="onChartLeave"
            @mousedown="onChartMouseDown"
            @mousemove="onChartMouseMove"
            @mouseleave="onChartLeave"
            @mouseup="onChartLeave"
          >
            <!-- baseline price reference line -->
            <line
              v-if="baselineY !== null"
              :x1="0"
              :y1="baselineY"
              :x2="chartWidth"
              :y2="baselineY"
              class="baselineLine"
            />

            <!-- price line -->
            <polyline :points="chartLinePoints" class="priceLine" />

            <!-- area fill under line -->
            <polygon :points="chartAreaPoints" class="priceArea" />

            <!-- trade markers -->
            <g v-for="(m, idx) in chartMarkers" :key="'marker-' + idx">
              <circle
                :cx="m.x"
                :cy="m.y"
                r="4"
                :class="m.side === 'buy' ? 'markerBuy' : 'markerSell'"
              />
            </g>

            <!-- crosshair guide + highlighted point, shown while touching/dragging -->
            <g v-if="activePoint">
              <line
                :x1="activePoint.x"
                :y1="0"
                :x2="activePoint.x"
                :y2="chartHeight"
                class="crosshairLine"
              />
              <circle
                :cx="activePoint.x"
                :cy="activePoint.y"
                r="4.5"
                class="crosshairDot"
              />
            </g>
          </svg>

          <!-- tooltip -->
          <div
            v-if="activePoint"
            class="chartTooltip"
            :style="tooltipStyle"
          >
            <div class="tooltipPrice">{{ activePoint.close }}</div>
            <div class="tooltipTime">{{ activePoint.timeLabel }}</div>
          </div>
        </div>

        <div class="chartAxisRow">
          <span>{{ chartFirstTime }}</span>
          <span>{{ chartLastTime }}</span>
        </div>

        <div class="chartLegend">
          <span class="legendItem"><i class="legendDotBuy"></i> Buy</span>
          <span class="legendItem"><i class="legendDotSell"></i> Sell</span>
          <span class="legendItem" v-if="baselineY !== null"><i class="legendDash"></i> Baseline {{ detail.baseline_price }}</span>
        </div>
      </div>

      <!-- Cash flow -->
      <div class="sectionCard" v-if="detail.cash_flow">
        <div class="sectionTitle">Cash Flow</div>
        <div class="statGrid">
          <div class="statItem">
            <div class="statLabel">Deposited</div>
            <div class="statValue">{{ detail.cash_flow.summary.deposited }}</div>
          </div>
          <div class="statItem">
            <div class="statLabel">Net Trading Cash</div>
            <div class="statValue" :class="pnlClass(detail.cash_flow.summary.net_trading_cash)">
              {{ formatPnl(detail.cash_flow.summary.net_trading_cash) }}
            </div>
          </div>
          <div class="statItem">
            <div class="statLabel">Final Balance</div>
            <div class="statValue">{{ detail.cash_flow.summary.final_balance }}</div>
          </div>
          <div class="statItem">
            <div class="statLabel">Reconciled</div>
            <div class="statValue">{{ detail.cash_flow.summary.reconciled ? 'Yes' : 'No' }}</div>
          </div>
        </div>

        <van-collapse v-model="activeCollapse">
          <van-collapse-item title="Cash Flow Entries" name="cashflow">
            <div class="flowEntry" v-for="entry in detail.cash_flow.entries" :key="entry.id">
              <div class="flowTop">
                <span class="flowLabel">{{ entry.label }}</span>
                <span class="flowDelta" :class="pnlClass(entry.cash_delta)">{{ formatPnl(entry.cash_delta) }}</span>
              </div>
              <div class="flowMeta">{{ formatDate(entry.at) }} · Balance: {{ entry.balance }}</div>
            </div>
          </van-collapse-item>
        </van-collapse>
      </div>

    </div>
  </div>
</template>

<script>
import CommonLoading from "./CommonLoading";

const CHART_WIDTH = 320;
const CHART_HEIGHT = 120;
const CHART_PADDING = 8;

export default {
  name: "AITradeMandateDetail",
  components: {
    CommonLoading,
  },
  data() {
    return {
      reference: "",
      detail: null,
      detailLoading: false,
      activeCollapse: [],
      chartWidth: CHART_WIDTH,
      chartHeight: CHART_HEIGHT,
      activePoint: null, // { x, y, close, timeLabel, screenX, screenY }
      isDragging: false,
    };
  },
  computed: {
    pnlRows() {
      if (!this.detail || !this.detail.pnl_summary) return {};
      const s = this.detail.pnl_summary;
      return {
        "Portfolio PnL": s.portfolio_pnl,
        "Trade Realized PnL": s.trade_realized_pnl,
        "Open Lots Unrealized PnL": s.open_lots_unrealized_pnl,
        "Entrusted Mark PnL": s.entrusted_mark_pnl,
        "Portfolio PnL At Last Trade": s.portfolio_pnl_at_last_trade,
      };
    },

    chartPoints() {
      if (!this.detail || !this.detail.recent_chart || !this.detail.recent_chart.candles) return [];
      return this.detail.recent_chart.candles;
    },

    chartPriceRange() {
      const closes = this.chartPoints.map((c) => c.close);
      if (this.detail && this.detail.baseline_price !== undefined) {
        closes.push(Number(this.detail.baseline_price));
      }
      let min = Math.min(...closes);
      let max = Math.max(...closes);
      if (min === max) {
        min -= 1;
        max += 1;
      }
      const pad = (max - min) * 0.1;
      return { min: min - pad, max: max + pad };
    },

    chartXForIndex() {
      const n = this.chartPoints.length;
      const usableWidth = this.chartWidth - CHART_PADDING * 2;
      return (idx) => {
        if (n <= 1) return CHART_PADDING;
        return CHART_PADDING + (idx / (n - 1)) * usableWidth;
      };
    },

    chartYForPrice() {
      const { min, max } = this.chartPriceRange;
      const usableHeight = this.chartHeight - CHART_PADDING * 2;
      return (price) => {
        if (max === min) return this.chartHeight / 2;
        const ratio = (price - min) / (max - min);
        return this.chartHeight - CHART_PADDING - ratio * usableHeight;
      };
    },

    chartLinePoints() {
      return this.chartPoints
        .map((c, idx) => `${this.chartXForIndex(idx)},${this.chartYForPrice(c.close)}`)
        .join(" ");
    },

    chartAreaPoints() {
      if (!this.chartPoints.length) return "";
      const line = this.chartPoints
        .map((c, idx) => `${this.chartXForIndex(idx)},${this.chartYForPrice(c.close)}`)
        .join(" ");
      const lastX = this.chartXForIndex(this.chartPoints.length - 1);
      const firstX = this.chartXForIndex(0);
      return `${firstX},${this.chartHeight} ${line} ${lastX},${this.chartHeight}`;
    },

    baselineY() {
      if (!this.detail || this.detail.baseline_price === undefined) return null;
      return this.chartYForPrice(Number(this.detail.baseline_price));
    },

    chartMarkers() {
      if (!this.detail || !this.detail.trade_markers || !this.chartPoints.length) return [];
      return this.detail.trade_markers.map((marker) => {
        const markerTime = new Date(marker.ts).getTime();
        let closestIdx = 0;
        let closestDiff = Infinity;
        this.chartPoints.forEach((c, idx) => {
          const diff = Math.abs(new Date(c.ts).getTime() - markerTime);
          if (diff < closestDiff) {
            closestDiff = diff;
            closestIdx = idx;
          }
        });
        return {
          side: marker.side,
          x: this.chartXForIndex(closestIdx),
          y: this.chartYForPrice(marker.price),
        };
      });
    },

    chartFirstTime() {
      if (!this.chartPoints.length) return "";
      return this.formatTimeOnly(this.chartPoints[0].ts);
    },

    chartLastTime() {
      if (!this.chartPoints.length) return "";
      return this.formatTimeOnly(this.chartPoints[this.chartPoints.length - 1].ts);
    },

    livePriceClass() {
      if (!this.detail || !this.detail.recent_chart) return "";
      const live = Number(this.detail.recent_chart.live_price);
      const base = Number(this.detail.baseline_price);
      if (isNaN(live) || isNaN(base)) return "";
      if (live > base) return "profitPositive";
      if (live < base) return "profitNegative";
      return "";
    },


    tooltipStyle() {
      if (!this.activePoint) return {};
      const leftPct = (this.activePoint.x / this.chartWidth) * 100;
      const topPct = (this.activePoint.y / this.chartHeight) * 100;

      const isRightHalf = leftPct > 60;

      const style = {
        top: `${topPct}%`,
      };
      if (isRightHalf) {
        style.right = `${100 - leftPct}%`;
        style.left = "auto";
        style.transform = "translate(8px, -50%)";
      } else {
        style.left = `${leftPct}%`;
        style.transform = "translate(-8px, -50%) translateX(-100%)";
      }
      return style;
    },
  },
  methods: {
    getMandateDetail() {
      let that = this;
      that.detailLoading = true;
      that
        .$http({
          url: "/",
          method: "post",
          data: {
            nozzle: "mandate_data",
            token: sessionStorage.getItem("token"),
            reference: that.reference,
          },
        })
        .then(function (res) {
          if (res.data.code == 1) {
            that.detail = res.data.data;
          } else if (res.data.code == 0 || res.data.code == -1) {
            that.$toast.fail(res.data.msg);
          }
        })
        .finally(() => {
          that.detailLoading = false;
        });
    },

    pnlClass(val) {
      const n = Number(val);
      if (isNaN(n) || val === null) return "";
      if (n > 0) return "profitPositive";
      if (n < 0) return "profitNegative";
      return "";
    },

    formatPnl(val) {
      if (val === null || val === undefined) return "-";
      const n = Number(val);
      if (isNaN(n)) return val;
      return (n > 0 ? "+" : "") + n;
    },

    formatDate(iso) {
      if (!iso) return "-";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      const pad = (x) => String(x).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },

    formatTimeOnly(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      const pad = (x) => String(x).padStart(2, "0");
      return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },

    statusTagType(status) {
      if (status === "active") return "success";
      if (status === "closed") return "primary";
      if (status === "completed") return "warning";
      return "warning";
    },

    riskTagType(risk) {
      if (risk === "normal") return "primary";
      if (risk === "high") return "danger";
      return "warning";
    },

    // ---- Chart interaction ----
    
    updateActivePointFromClientX(clientX) {
      const svg = this.$refs.chartWrap && this.$refs.chartWrap.querySelector("svg");
      if (!svg || !this.chartPoints.length) return;

      const rect = svg.getBoundingClientRect();
      if (rect.width === 0) return;

      // clientX relative to svg, converted from pixel space into viewBox space
      const relativeX = clientX - rect.left;
      const svgX = (relativeX / rect.width) * this.chartWidth;

      // find nearest candle by comparing its plotted X position
      let closestIdx = 0;
      let closestDiff = Infinity;
      this.chartPoints.forEach((c, idx) => {
        const px = this.chartXForIndex(idx);
        const diff = Math.abs(px - svgX);
        if (diff < closestDiff) {
          closestDiff = diff;
          closestIdx = idx;
        }
      });

      const point = this.chartPoints[closestIdx];
      this.activePoint = {
        x: this.chartXForIndex(closestIdx),
        y: this.chartYForPrice(point.close),
        close: point.close,
        timeLabel: this.formatTimeOnly(point.ts),
      };
    },

    onChartTouch(e) {
      if (!e.touches || !e.touches.length) return;
      // prevent the page from scrolling while dragging a finger across the chart
      e.preventDefault();
      this.updateActivePointFromClientX(e.touches[0].clientX);
    },

    onChartMouseDown(e) {
      this.isDragging = true;
      this.updateActivePointFromClientX(e.clientX);
    },

    onChartMouseMove(e) {
      if (!this.isDragging) return;
      this.updateActivePointFromClientX(e.clientX);
    },

    onChartLeave() {
      this.isDragging = false;
      this.activePoint = null;
    },
  },
  mounted: function () {
    this.reference = this.$route.query.reference;
    this.getMandateDetail();
  },
};
</script>

<style scoped>
.page {
  background: #f7f8fa;
  min-height: 100vh;
}

.content {
  padding: 12px 12px 30px;
}

.headerCard {
  background: #ffffff;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.headerTop {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.symbol {
  font-size: 18px;
  font-weight: 700;
  color: #000;
}

.reference {
  font-size: 11px;
  color: #969799;
  margin-top: 2px;
  word-break: break-all;
}

.priceRow {
  display: flex;
  justify-content: space-between;
  margin-top: 14px;
}

.priceBlock {
  text-align: center;
  flex: 1;
}

.priceLabel {
  font-size: 12px;
  color: #969799;
}

.priceValue {
  font-size: 16px;
  font-weight: 600;
  margin-top: 2px;
  color: #000;
}

.datesRow {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  font-size: 12px;
  color: #646566;
}

.sectionCard {
  background: #ffffff;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.sectionTitle {
  font-size: 14px;
  font-weight: 700;
  color: #000;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.chartInterval {
  font-size: 11px;
  font-weight: 500;
  color: #969799;
  background: #f2f3f5;
  padding: 1px 6px;
  border-radius: 4px;
}

.chartPriceRow {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 8px;
}

.chartLivePrice {
  font-size: 20px;
  font-weight: 700;
  color: #000;
}

.chartLiveLabel {
  font-size: 11px;
  color: #969799;
}

.chartWrap {
  position: relative;
  width: 100%;
}

.chartSvg {
  width: 100%;
  height: 120px;
  display: block;
  touch-action: none; /* prevent page scroll while dragging on the chart */
}

.priceLine {
  fill: none;
  stroke: #1989fa;
  stroke-width: 1.5;
}

.priceArea {
  fill: rgba(25, 137, 250, 0.08);
  stroke: none;
}

.baselineLine {
  stroke: #c8c9cc;
  stroke-width: 1;
  stroke-dasharray: 3 3;
}

.markerBuy {
  fill: #07c160;
  stroke: #ffffff;
  stroke-width: 1;
}

.markerSell {
  fill: #ee0a24;
  stroke: #ffffff;
  stroke-width: 1;
}

.crosshairLine {
  stroke: #c8c9cc;
  stroke-width: 1;
  stroke-dasharray: 2 2;
}

.crosshairDot {
  fill: #1989fa;
  stroke: #ffffff;
  stroke-width: 1.5;
}

.chartTooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.8);
  color: #ffffff;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 5;
}

.tooltipPrice {
  font-weight: 700;
  font-size: 12px;
  text-align: center;
}

.tooltipTime {
  color: #c8c9cc;
  text-align: center;
  margin-top: 1px;
}

.chartAxisRow {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #969799;
  margin-top: 4px;
}

.chartLegend {
  display: flex;
  gap: 14px;
  margin-top: 8px;
}

.legendItem {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #646566;
}

.legendDotBuy,
.legendDotSell {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.legendDotBuy {
  background: #07c160;
}

.legendDotSell {
  background: #ee0a24;
}

.legendDash {
  width: 10px;
  height: 0;
  border-top: 1px dashed #c8c9cc;
  display: inline-block;
}

.statGrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.statItem {
  background: #f7f8fa;
  border-radius: 8px;
  padding: 8px 10px;
}

.statLabel {
  font-size: 11px;
  color: #969799;
}

.statValue {
  font-size: 14px;
  font-weight: 600;
  color: #000;
  margin-top: 2px;
}

.flowEntry {
  padding: 8px 0;
  border-bottom: 1px solid #f2f2f2;
}

.flowEntry:last-child {
  border-bottom: none;
}

.flowTop {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
}

.flowLabel {
  color: #000;
}

.flowDelta {
  font-weight: 600;
}

.flowMeta {
  font-size: 11px;
  color: #969799;
  margin-top: 2px;
}

.profitPositive {
  color: #07c160;
}

.profitNegative {
  color: #ee0a24;
}
</style>


// WEBPACK FOOTER //
// src/components/AITradeDepotMandateDetail.vue